// src/components/dashboard/charts/SectorEnergyVectorChart.js
import React, { useMemo } from "react";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale, LinearScale, PointElement, LineElement,
  Tooltip, Legend, Title, Filler,
} from "chart.js";

import FOOD from "../../../data/foodEnergyVectors";
import TRANSPORT from "../../../data/transportEnergyVectors";
import BUILDINGS from "../../../data/buildingsEnergyVectors"; // ← fix typo (BUILDINGS, not BULDINGS)
import INDUSTRY from "../../../data/industryEnergyVectors";
import { getEnergyColor } from "../../../config/colorScheme";

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Tooltip, Legend, Title, Filler);

function pickStaticTable({ sectorCodes = [] }) {
  // crude sector → table mapping
  const wanted = new Set((sectorCodes || []).map(s => String(s).toUpperCase()));
  // FOOD wins if explicitly present
  if (wanted.has("AGR")) return FOOD;
  // Transport aliases
  if ([...wanted].some(s => s.startsWith("TRA") || s === "TRANSPORT")) return TRANSPORT;
  // Industry aliases
  if ([...wanted].some(s => s.startsWith("IND") || s === "INDUSTRY" || s.startsWith("MAN"))) return INDUSTRY;
  // Buildings: RES/SER/COM/BLD
  if ([...wanted].some(s => ["RES","SER","COM","BLD","BUILDINGS","BUI"].includes(s))) return BUILDINGS;
  // default to buildings if we can’t tell
//   return BUILDINGS;
}

// normalize keys (lowercase, strip spaces/underscores/dashes)
const nk = (k) => String(k || "").toLowerCase().replace(/[\s\-_]/g, "");

// safe value pick with synonyms
function valFor(row = {}, keys = []) {
  const map = new Map();
  for (const [k, v] of Object.entries(row)) map.set(nk(k), Number(v) || 0);
  for (const k of keys) {
    const v = map.get(nk(k));
    if (v != null) return v;
  }
  return 0;
}

export default function SectorEnergyVectorChart({
  scenario,
  sectorCodes = ["RES"],

  from = 2025,
  to = 2050,
  title = "Energy Demand by Vector (TWh)",
  includeOther = true,
  showDebug = false,
}) {
  // pick the right static source
  const table = useMemo(() => pickStaticTable({ sectorCodes }), [sectorCodes]);

  // turn the scenario’s rows into labels + stacked vector series
  const { labels, vectorSeries } = useMemo(() => {
    const rowsByYear = table?.[scenario] || {};
    const yearsAll = Object.keys(rowsByYear).map(Number).filter(Number.isFinite).sort((a,b)=>a-b);
    const years = yearsAll.filter(y => y >= from && y <= to);

    const K = {
      electricity: ["electricity","elec"],
      hydrogen:    ["hydrogen","h2"],
      fossil:      ["fossil","fossilfuels","fossils"],
      biofuels:    ["biofuels","bioenergy","bio"],
      other:       ["other","others","oth"],
    };

    const series = years.map(y => {
      const row = rowsByYear[y] || {};
      return {
        electricity: valFor(row, K.electricity),
        hydrogen:    valFor(row, K.hydrogen),
        fossil:      valFor(row, K.fossil),
        biofuels:    valFor(row, K.biofuels),
        other:       valFor(row, K.other),
      };
    });

    if (showDebug) {
      const sampleY = years[years.length - 1];
      // eslint-disable-next-line no-console
      console.log("[SectorVectors]", { scenario, sectorCodes, sampleYear: sampleY, sampleRow: rowsByYear[sampleY] });
    }

    return { labels: years.map(String), vectorSeries: series };
  }, [table, scenario, from, to, showDebug]);

  // build datasets (thin lines)
  const chart = useMemo(() => {
    const y = vectorSeries || [];
    const electricity = y.map(r => r.electricity || 0);
    const hydrogen    = y.map(r => r.hydrogen || 0);
    const fossil      = y.map(r => r.fossil || 0);
    const other       = y.map(r => r.other || 0);
    const biofuels    = y.map(r => r.biofuels || 0);

    const ds = [
      {
        label: "Electricity",
        data: electricity,
        borderColor: getEnergyColor?.("electricity","primary") ?? "#3b82f6",
        backgroundColor: getEnergyColor?.("electricity","light") ?? "rgba(59,130,246,0.25)",
        borderWidth: 1,
        fill: true, tension: 0.25, pointRadius: 0,
      },
      {
        label: "Hydrogen",
        data: hydrogen,
        borderColor: getEnergyColor?.("hydrogen","primary") ?? "#06b6d4",
        backgroundColor: getEnergyColor?.("hydrogen","light") ?? "rgba(6,182,212,0.25)",
        borderWidth: 1,
        fill: true, tension: 0.25, pointRadius: 0,
      },
      {
        label: "Fossil fuels",
        data: fossil,
        borderColor: getEnergyColor?.("fossil","primary") ?? "#ef4444",
        backgroundColor: getEnergyColor?.("fossil","light") ?? "rgba(239,68,68,0.25)",
        borderWidth: 1,
        fill: true, tension: 0.25, pointRadius: 0,
      },
      {
        label: "Bioenergy",
        data: biofuels,
        borderColor: getEnergyColor?.("biofuels","primary") ?? "#a855f7",
        backgroundColor: getEnergyColor?.("biofuels","light") ?? "rgba(168,85,247,0.20)",
        borderWidth: 1,
        fill: true, tension: 0.25, pointRadius: 0,
      },
    ];


      ds.push({
        label: "Other",
        data: other,
        borderColor: getEnergyColor?.("other","primary") ?? "#8b5cf6",
        backgroundColor: getEnergyColor?.("other","light") ?? "rgba(139,92,246,0.20)",
        borderWidth: 1,
        fill: true, tension: 0.25, pointRadius: 0,
      });


    return { labels, datasets: ds };
  }, [labels, vectorSeries, includeOther]);

  if (!labels?.length) {
    return <div className="w-full h-full text-xs text-gray-600">No data for this sector/scenario in range.</div>;
  }

  return (
    <Line
      data={chart}
      options={{
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          title: { display: true, text: title, font: { size: 14 } },
          legend: { position: "bottom" },
          tooltip: { mode: "index", intersect: false },
        },
        scales: {
          x: { title: { display: true, text: "Year" } },
          y: { title: { display: true, text: "TWh" },   min: 0,stacked: true, beginAtZero: true },
        },
        interaction: { mode: "index", intersect: false },
      }}
    />
  );
}
